import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { useTheme } from "@/context/ThemeContext";
import { Card, Metric, SectionLabel, DataStatusBadge, Pill } from "@/components/common";
import { EconomicCalendar } from "@/components/charts/EconomicCalendar";
import { Sunrise, ShieldCheck, ShieldAlert, Lock, CalendarClock } from "lucide-react";

function SafetyRecap({ safety }) {
  if (!safety) return null;
  const cb = safety.circuit_breaker;
  const cg = safety.capital_guard;
  let tone, Icon, title, text;
  if (cb?.locked) {
    tone = "#FF4D67"; Icon = Lock; title = "Trading locked — protect capital";
    text = `Aurum has locked new entries today: ${(cb.reasons || []).join("; ") || "daily risk limit reached"}. Step away and preserve your account.`;
  } else if (cg && cg.max_loss_usd != null) {
    tone = "#21C58E"; Icon = ShieldCheck; title = "Your max loss today";
    text = `If you take today's ${safety.direction || ""} setup with the recommended stop and volume, your maximum loss is $${cg.max_loss_usd}${cg.max_loss_pct != null ? ` (${cg.max_loss_pct}% of your account)` : ""}. The stop is your hard floor — a sudden reversal cannot cost more than this.`;
  } else {
    tone = "#98A2B3"; Icon = ShieldAlert; title = "No setup — safest position is none";
    const cap = safety.max_risk_per_trade != null
      ? `${safety.max_risk_per_trade}% of your account`
      : "your configured risk per trade";
    text = `No qualifying setup right now, so there's nothing to risk. When one appears, Aurum caps any trade you take at ${cap}.`;
  }
  return (
    <Card className="p-4" data-testid="safety-recap" style={{ borderColor: `${tone}44` }}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0"><Icon size={18} style={{ color: tone }} /></div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider" style={{ color: tone }}>{title}</div>
          <p className="mt-1 text-sm text-[var(--text-sec)]">{text}</p>
        </div>
      </div>
    </Card>
  );
}

export default function DailyBrief() {
  const { data } = usePolling(async () => (await api.get("/analysis/daily-brief")).data, 30000, []);
  const { theme } = useTheme();
  if (!data) return <div className="text-sm text-[var(--text-muted)]">Loading daily brief…</div>;
  return (
    <div className="space-y-5 max-w-4xl animate-fade-up">
      <div className="flex items-center gap-3">
        <Sunrise className="text-aurum" />
        <h1 className="text-2xl font-bold tracking-tight">Daily Gold Brief</h1>
        <DataStatusBadge status={data.data_status} />
      </div>
      <SafetyRecap safety={data.safety} />
      <Card className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Metric label="XAU/USD" value={`$${data.price?.toFixed(2)}`} color="#D6AE57" />
          <Metric label="Prev Day High" value={data.prev_day_high?.toFixed(2)} />
          <Metric label="Prev Day Low" value={data.prev_day_low?.toFixed(2)} />
          <Metric label="Regime" value={data.regime} mono={false} />
        </div>
        <div className="mt-5 pt-5 border-t border-[var(--border2)] flex flex-wrap items-center gap-3">
          <SectionLabel>Session:</SectionLabel><span className="text-sm font-medium">{data.current_session.join(" + ")}</span>
          <SectionLabel>HTF Structure:</SectionLabel><span className="text-sm font-medium">{data.htf_structure}</span>
        </div>
      </Card>
      <div className="grid md:grid-cols-2 gap-5">
        <Card className="p-5">
          <SectionLabel>Major Support</SectionLabel>
          <div className="mt-3 space-y-2">
            {data.major_support.length ? data.major_support.map((l, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="tnum font-medium">{l.price.toFixed(2)}</span>
                <Pill color="#21C58E">{l.rank} · {l.touches}x</Pill>
              </div>
            )) : <div className="text-xs text-[var(--text-muted)]">No major support detected nearby.</div>}
          </div>
        </Card>
        <Card className="p-5">
          <SectionLabel>Major Resistance</SectionLabel>
          <div className="mt-3 space-y-2">
            {data.major_resistance.length ? data.major_resistance.map((l, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="tnum font-medium">{l.price.toFixed(2)}</span>
                <Pill color="#EF6262">{l.rank} · {l.touches}x</Pill>
              </div>
            )) : <div className="text-xs text-[var(--text-muted)]">No major resistance detected nearby.</div>}
          </div>
        </Card>
      </div>
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <CalendarClock size={16} className="text-aurum" />
          <SectionLabel>Economic Calendar — high-impact events</SectionLabel>
        </div>
        <p className="text-xs text-[var(--text-muted)] mb-3">Live releases that move gold (USD & major economies). Avoid entering right into a red event — Aurum also pauses new-entry validation around them.</p>
        <EconomicCalendar theme={theme} height={520} />
      </Card>
      <p className="text-xs text-[var(--text-muted)]">This brief is educational and does not make guaranteed predictions. Trading involves substantial risk.</p>
    </div>
  );
}
